import { useEffect, useState } from 'react';
import { useQueryClient, useJobStatus } from 'shared-ui';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { yupResolver } from '@hookform/resolvers/yup';

import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { AppMessages } from 'i18n/messages';
import { fetchServers } from 'api/servers/servers';
import { fetchTerminals } from 'api/terminals/terminals';
import { useQuery } from 'hooks/useQuery/useQuery';
import { useMutation } from 'hooks/useMutation/useMutation';
import { addMultiPermissions } from 'api/groups/groups';
import { DEFAULT_PERMISSIONS } from 'core/global.variables';
import { GlcNumberSortEnum, GroupTerminalsPermissionsIdSortEnum } from 'context/query/queryContext/QueryContext.enum';
import { fetchGlc } from 'api/glc/glc';
import { useModalState } from 'hooks/useModalState/useModalState';
import { JobIdResponse } from 'api/jobs/jobs.types';

import { useAddMulitGroupPermissionsValidation } from './AddMultiGroupPermissions.validation';
import {
  AddMultiGroupPermissionsFormBody,
  AddMultiGroupPermissionsContainerProps,
} from './AddMultiGroupPermissions.types';
import { AddMultiGroupPermissions } from './AddMultiGroupPermissions';

export const AddMultiGroupPermissionsContainer = ({ checkedGroups }: AddMultiGroupPermissionsContainerProps) => {
  const queryClient = useQueryClient();
  const { formatMessage } = useLocale();
  const [selectedServerId, setSelectedServerId] = useState('');
  const [terminalsQuery] = useState('');
  const areGlcCheckedGroups = checkedGroups.every(({ glc }) => glc);
  const { schema } = useAddMulitGroupPermissionsValidation(areGlcCheckedGroups);
  const { isModalOpen, onModalOpen, onModalClose } = useModalState();
  const handleJobComplete = () => {
    queryClient.invalidateQueries([QueryKeyEnum.GROUPS]);
    toast.dark(formatMessage({ id: AppMessages['groups.multiAction.addPermissions.successMessage'] }));
  };

  const handleJobError = (error: string) => {
    toast.error(formatMessage({ id: AppMessages['common.error'] }) || `Error: ${error}`);
  };

  const jobStatus = useJobStatus({
    useGlobalModal: true,
    autoClose: false,
    onComplete: handleJobComplete,
    onError: handleJobError,
    showItemCount: false,
    customCompleteMessage: formatMessage({ id: AppMessages['groups.result.success'] }),
    customFailedMessage: formatMessage({ id: AppMessages['groups.result.failed'] }),
    customActiveMessage: formatMessage({ id: AppMessages['groups.result.waiting'] }),
    title: formatMessage({ id: AppMessages['groups.result.multiGroupsTitle'] }),
  });

  const methods = useForm<AddMultiGroupPermissionsFormBody>({
    defaultValues: {
      ...DEFAULT_PERMISSIONS,
      idTkds: [],
    },
    mode: 'all',
    resolver: yupResolver(schema),
  });

  const { data: serversResponse, isLoading: isLoadingServers } = useQuery([QueryKeyEnum.SERVERS], fetchServers, {
    onSuccess: (serversResponse) => {
      if (serversResponse.data.length) {
        setSelectedServerId(serversResponse.data[0].id);
      }
    },
  });

  const { data: terminalsResponse, isInitialLoading: isLoadingTerminals } = useQuery(
    [QueryKeyEnum.TERMINALS, selectedServerId, terminalsQuery],
    () => fetchTerminals(selectedServerId),
    {
      args: {
        zaakceptowane: true,
        sort: [GroupTerminalsPermissionsIdSortEnum.ASC],
        withoutGlc: true,
      },
      enabled: !!selectedServerId && !areGlcCheckedGroups,
    },
  );

  const { data: glcResponse, isInitialLoading: isLoadingGlcGroups } = useQuery([QueryKeyEnum.GLC], fetchGlc, {
    args: { sort: [GlcNumberSortEnum.ASC] },
    enabled: !!selectedServerId && areGlcCheckedGroups,
  });

  const { mutate: onAddMultiPermissions, isLoading } = useMutation(addMultiPermissions, {
    onSuccess: (response: JobIdResponse) => {
      if (!response || !response.jobId) {
        toast.error(formatMessage({ id: AppMessages['common.error'] }));
        return;
      }

      jobStatus.startJob(response.jobId);
      onModalClose();
    },
    onError: (error: unknown) => {
      const errorObj = error as Record<string, unknown>;
      toast.error(
        formatMessage({ id: AppMessages['common.error'] }) || `Error: ${errorObj?.message || 'Unknown error'}`,
      );
    },
  });

  const onSubmit = (body: AddMultiGroupPermissionsFormBody) => {
    const requestBody = {
      ...body,
      idTkds: body.idTkds?.map(({ value }) => `${value}`) || [],
      idGlcs: body.idGlcs?.map(({ value }) => `${value}`) || [],
      idGrup: checkedGroups.map(({ id }) => id),
    };

    onAddMultiPermissions(requestBody);
  };

  useEffect(() => {
    methods.reset();
  }, [isModalOpen, selectedServerId]);

  return (
    <FormProvider {...methods}>
      <AddMultiGroupPermissions
        onSubmit={onSubmit}
        isModalOpen={isModalOpen}
        onModalOpen={onModalOpen}
        onModalClose={onModalClose}
        isLoading={isLoading}
        servers={serversResponse?.data || []}
        selectedServerId={selectedServerId}
        setSelectedServerId={setSelectedServerId}
        isLoadingTerminals={isLoadingTerminals}
        terminals={terminalsResponse?.data || []}
        glcGroups={glcResponse?.data || []}
        isLoadingGlcGroups={isLoadingGlcGroups}
        isLoadingServers={isLoadingServers}
        areGlcCheckedGroups={areGlcCheckedGroups}
      />
    </FormProvider>
  );
};
